import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { AlertTriangle, ArrowLeft, CarFront, CheckCircle, ChevronDown, Home, Mail, MessageSquare, Plane, Send, XCircle } from 'lucide-react';
import { differenceInDays, format, isValid, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { env } from '@/config/env';
import {
  useEnableNotificationsMutation,
  useGetFoliosQueries,
  useGuestCommentsQuery,
  useGuestNotificationsQuery,
  useSendCommentsMutation,
  useSendNotificationsMutation,
} from '@/services/reservations';
import { trackAction, trackPageView } from '@/utils/analytics';
import { cn } from '@/utils/cn';
import type {
  CommentResponse,
  CommentsApiRequest,
  FlightSegment,
  GetFoliosResponse,
  GroundTransportationBooking,
  GroundTransportationLeg,
  HotelReservation,
  ReservationFolio,
  ReservationWebResponse,
} from '@/features/dashboard/types';

const BUSINESS_ATTR = 'Business (MDR/OPM Data)';
const CITY_ATTR = 'Office/City';
const DEFAULT_USER_IMAGE = '/assets/imgs/default-user.png';

function getProfilePictureUrl(email: string | null | undefined) {
  if (!email) return DEFAULT_USER_IMAGE;
  return env.VITE_PROFILES_API.replace('{0}', email.toUpperCase());
}
// Set to true to re-enable the comment delete button in the UI
const SHOW_COMMENT_DELETE = false;

type DisplayValue = string | number | boolean | null | undefined;

function getStoredString(key: string) {
  return globalThis.sessionStorage?.getItem(key) ?? '';
}

function getStoredReservation() {
  const storedReservation = globalThis.sessionStorage?.getItem('reservation');
  if (!storedReservation) return null;

  try {
    return JSON.parse(storedReservation) as ReservationWebResponse;
  } catch {
    return null;
  }
}

function hasValue(value: DisplayValue) {
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return true;
  return Boolean(value?.trim() && value.trim().toLowerCase() !== 'null');
}

function displayValue(value: DisplayValue) {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  return hasValue(value) ? value : '-';
}

function formatDate(value: string | null | undefined, dateFormat = 'M/d/yyyy') {
  if (!value || !hasValue(value)) return '-';
  const parsedDate = parseISO(value);
  return isValid(parsedDate) ? format(parsedDate, dateFormat) : value;
}

function formatDateTime(value: string | null | undefined) {
  return formatDate(value, 'M/d/yyyy hh:mm a');
}

function getProfileQuestion(reservation: ReservationWebResponse, questionName: string) {
  return reservation.certainResponseObj?.profile?.profileQuestions?.question
    ?.find((question) => question.questionName?.trim().toLowerCase() === questionName.toLowerCase())
    ?.answers?.answer?.[0]?.value;
}

function issueStatusClass(colorCode: string | null | undefined) {
  if (colorCode === 'issue') return 'border-dds-red bg-dds-red text-white';
  if (colorCode === 'warning') return 'bg-amber-500 text-white';
  if (colorCode === 'disabled') return 'border-cool-gray-2 bg-gray-300 text-black';
  return 'border-cool-gray-2 bg-gray-300 text-black';
}

function issuePillClass(colorCode: string | null | undefined) {
  if (colorCode === 'issue') return 'border-dds-red text-dds-red';
  if (colorCode === 'warning') return 'border-amber-500 text-amber-500';
  return 'border-cool-gray-2 text-cool-gray-2';
}

function getStatusLabel(primary: string | null | undefined, fallback: string | null | undefined) {
  return hasValue(primary) ? primary : fallback;
}

function getFolioLookupKey(
  folio: ReservationFolio | undefined,
  reservation: HotelReservation | undefined,
) {
  return [folio?.hMSReservationReference, reservation?.reservationId, folio?.reservationID].find(
    (value) => hasValue(value),
  );
}

// A guest is back-to-back when any reservation's departure date equals the next one's arrival date
function computeIsBackToBack(reservations: HotelReservation[]): boolean {
  const dated = reservations
    .filter((r) => r.arrival && r.departure)
    .sort((a, b) => (a.arrival! < b.arrival! ? -1 : 1));
  for (let i = 0; i < dated.length - 1; i++) {
    const dep = dated[i]?.departure?.slice(0, 10);
    const arr = dated[i + 1]?.arrival?.slice(0, 10);
    if (dep && arr && dep === arr) return true;
  }
  return false;
}

export function GuestDetailsPage() {
  const navigate = useNavigate();
  const [guestInfo, setGuestInfo] = useState<ReservationWebResponse | null>(() => getStoredReservation());
  const [commentText, setCommentText] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const storedReservation = getStoredReservation();
    return Boolean(storedReservation?.enableNotifications && !storedReservation.isClient);
  });
  const { accounts } = useMsal();
  const alias = getStoredString('Alias');
  const loggedInUserEmail = accounts[0]?.username ?? '';
  const loggedInUserName = accounts[0]?.name ?? '';
  const sendCommentsMutation = useSendCommentsMutation();
  const enableNotificationsMutation = useEnableNotificationsMutation();
  const sendNotificationsMutation = useSendNotificationsMutation();

  useEffect(() => {
    trackPageView('guest-details', alias, 'Travel');
  }, [alias]);

  useEffect(() => {
    if (!guestInfo) {
      setGuestInfo(getStoredReservation());
    }
  }, [guestInfo]);

  const eventId = guestInfo?.groupID ?? '';
  const registrationId = guestInfo?.registrationId ?? '';
  const email = guestInfo?.email ?? '';
  const commentsQuery = useGuestCommentsQuery({ email, eventId, registrationId });
  const notificationsQuery = useGuestNotificationsQuery(registrationId, eventId);
  const orderedComments = useMemo(
    () => [...(commentsQuery.data ?? [])].sort((left, right) => String(right.createdDate).localeCompare(String(left.createdDate))),
    [commentsQuery.data],
  );
  const orderedNotifications = useMemo(
    () => [...(notificationsQuery.data ?? [])].sort((left, right) => String(right.createdDate).localeCompare(String(left.createdDate))),
    [notificationsQuery.data],
  );

  const folioHmsNums = useMemo(() => {
    const folios = guestInfo?.reservationFolios ?? [];
    const reservationObj = guestInfo?.reservationObj ?? [];
    return folios.length > 0
      ? folios.map((folio, index) =>
          getFolioLookupKey(folio, reservationObj[folio.index ?? index]),
        )
      : reservationObj.map((res) => res.reservationId);
  }, [guestInfo]);

  const folioQueries = useGetFoliosQueries(folioHmsNums);

  const folioDataMap = useMemo(() => {
    const map = new Map<string, GetFoliosResponse>();
    folioHmsNums.forEach((hmsNum, index) => {
      const data = folioQueries[index]?.data;
      if (hmsNum && data) map.set(hmsNum, data);
    });
    return map;
  }, [folioHmsNums, folioQueries]);

  const isLoading = commentsQuery.isLoading || notificationsQuery.isLoading || folioQueries.some((q) => q.isLoading);

  if (!guestInfo) {
    return (
      <div className="space-y-4">
        <BackButton onBack={() => navigate('/dashboard')} />
        <div role="alert" className="border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          No guest reservation is selected. Return to the dashboard and open a guest again.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" aria-busy="true" aria-label="Loading guest details">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-cool-gray-2 border-t-dds-blue" />
      </div>
    );
  }

  const isOrphan = guestInfo.issueType?.trim().toLowerCase() === 'orphan';
  const canUseNotifications = Boolean(guestInfo.isElgibleForNotification && !guestInfo.isNotableGuest);
  // Order matches the Prod header row: Mobile Phone, Organization, Office/City, Business, Position...
  const headerFieldsAll: Array<[string, DisplayValue]> = [
    ['Mobile Phone', guestInfo.certainResponseObj?.profile?.phone] as [string, DisplayValue],
    ['Organization', guestInfo.organization] as [string, DisplayValue],
    ['Office/City', getProfileQuestion(guestInfo, CITY_ATTR) ?? guestInfo.officeCity] as [string, DisplayValue],
    ['Business', getProfileQuestion(guestInfo, BUSINESS_ATTR)] as [string, DisplayValue],
    ['Position', guestInfo.certainResponseObj?.profile?.jobPosition] as [string, DisplayValue],
    ['Alternative Email', guestInfo.prevEmail] as [string, DisplayValue],
    ['Assistant Name', guestInfo.certainResponseObj?.profile?.assistant?.assistantName] as [string, DisplayValue],
    ['Assistant Email', guestInfo.certainResponseObj?.profile?.assistant?.assistantEmail] as [string, DisplayValue],
  ];
  const headerFields = headerFieldsAll.filter(([, value]) => hasValue(value));

  async function handleNotificationPreferenceChange(checked: boolean) {
    if (!guestInfo) return;
    const success = await enableNotificationsMutation.mutateAsync({
      discrepancyIds: String(guestInfo.discrepancyId),
      notificationStatus: checked,
    });
    if (success) {
      setNotificationsEnabled(checked);
      trackAction('guest-details', alias, 'notification-turn-on-off', null, 'Travel');
      toast.success('User Notification Preferences Updated Successfully.');
    } else {
      toast.error('Unable to update notification preferences.');
    }
  }

  async function handleSendNotification() {
    if (!guestInfo) return;
    const success = await sendNotificationsMutation.mutateAsync({
      notifyUsers: String(guestInfo.discrepancyId),
      loggedInUserEmail,
    });
    if (success) {
      trackAction('guest-details', alias, 'send-notification', null, 'Travel');
      toast.success('Notification Sent Successfully.');
      await notificationsQuery.refetch();
    } else {
      toast.error('Send Notification Failed, Please contact DU ITS Team.');
    }
  }

  async function handleSendComment() {
    if (!guestInfo) return;
    const trimmedComment = commentText.trim();
    if (!trimmedComment) return;
    const request: CommentsApiRequest = {
      eventId,
      registrationId,
      fromEmail: loggedInUserEmail,
      toEmail: guestInfo.email ?? '',
      commentText: trimmedComment,
      fromName: loggedInUserName,
      toName: guestInfo.guestName ?? '',
      isRemoved: false,
    };
    const success = await sendCommentsMutation.mutateAsync([request]);
    if (success) {
      trackAction('guest-details', alias, 'send-comments', null, 'Travel');
      toast.success('Comments Sent Successfully.');
      setCommentText('');
      await commentsQuery.refetch();
    } else {
      toast.error('Send Comment Failed, Please contact DU ITS Team.');
    }
  }

  async function handleDeleteComment(comment: CommentResponse) {
    const confirmed = globalThis.confirm('Are you sure you want to delete?');
    if (!confirmed) return;
    const success = await sendCommentsMutation.mutateAsync([
      {
        id: comment.id,
        eventId: comment.eventId ?? eventId,
        registrationId: comment.registrationId ?? registrationId,
        fromEmail: comment.fromEmail ?? '',
        toEmail: comment.toEmail ?? '',
        commentText: comment.commentText ?? '',
        fromName: comment.fromName ?? '',
        toName: comment.toName ?? '',
        isRemoved: true,
      },
    ]);
    if (success) {
      toast.success('Comments Deleted Successfully.');
      await commentsQuery.refetch();
    } else {
      toast.error('Delete Comment Failed, Please contact DU ITS Team.');
    }
  }

  return (
    <div className="space-y-5">
      <BackButton onBack={() => navigate('/dashboard')} />
      <section aria-labelledby="guest-name-heading" className="border border-cool-gray-2 bg-white p-4">
        <div className="flex flex-col gap-4 overflow-x-auto lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 shrink-0 items-center gap-4">
            <img
              src={getProfilePictureUrl(guestInfo.email)}
              alt=""
              className="h-16 w-16 shrink-0 rounded-full border border-cool-gray-2 object-cover"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_USER_IMAGE;
              }}
            />
            <div className="min-w-0">
              <h1 id="guest-name-heading" className="truncate text-2xl font-semibold capitalize text-black">{displayValue(guestInfo.guestName)}</h1>
              <p className="truncate text-sm text-cool-gray-10">{displayValue(guestInfo.email)}</p>
            </div>
          </div>
          <dl className="flex shrink-0 flex-nowrap gap-x-6">
            {headerFields.map(([label, value]) => (
              <div key={label} className="shrink-0">
                <dt className="whitespace-nowrap text-xs font-semibold uppercase text-cool-gray-10">{label}</dt>
                <dd className="mt-1 whitespace-nowrap text-sm text-black" title={String(displayValue(value))}>
                  {displayValue(value)}
                </dd>
              </div>
            ))}
            <div className="shrink-0">
              <dt className="whitespace-nowrap text-xs font-semibold uppercase text-cool-gray-10">Is Notable Guest</dt>
              <dd className="mt-1 whitespace-nowrap text-sm text-black">
                {guestInfo.isNotableGuest ? 'Yes' : 'No'}
              </dd>
            </div>
          </dl>
        </div>
        <div className="my-4 border-t border-cool-gray-2" />
        <GuestStatusBar
          guestInfo={guestInfo}
          notificationsEnabled={notificationsEnabled}
          canUseNotifications={canUseNotifications}
          isSending={sendNotificationsMutation.isPending}
          onNotificationChange={(checked) => void handleNotificationPreferenceChange(checked)}
          onSend={() => void handleSendNotification()}
        />
      </section>

      <InfoCard title="Event Information">
        {hasValue(guestInfo.eventDetailObj?.eventId) ? (
          <InfoGrid
            inline
            items={[
              ['Event Name', guestInfo.certainResponseObj?.eventName],
              ['Event ID', guestInfo.eventDetailObj?.eventId],
              ['Start Date', formatDate(guestInfo.eventDetailObj?.eventStartDate)],
              ['End Date', formatDate(guestInfo.eventDetailObj?.eventEndDate)],
              ['Certain Account', guestInfo.certainResponseObj?.accountCode],
              ['Meeting Type', guestInfo.eventDetailObj?.meetingType],
            ]}
          />
        ) : (
          <EmptyState>There is no information available</EmptyState>
        )}
      </InfoCard>

      <section aria-label="Guest travel detail cards" className="grid items-stretch gap-4 lg:grid-cols-3">
        <RegistrationCard guestInfo={guestInfo} />
        <ReservationCard guestInfo={guestInfo} folioDataMap={folioDataMap} />
        <FlightCard guestInfo={guestInfo} />
        <GroundTransportCard guestInfo={guestInfo} />
        {!isOrphan && (
          <NotificationsCard
            isLoading={notificationsQuery.isFetching}
            notifications={orderedNotifications}
            notificationCount={guestInfo.notificationCount}
          />
        )}
        {!isOrphan && (
          <CommentsCard
            comments={orderedComments}
            commentText={commentText}
            currentUserEmail={loggedInUserEmail}
            isLoading={commentsQuery.isFetching}
            isSaving={sendCommentsMutation.isPending}
            onCommentTextChange={setCommentText}
            onDeleteComment={(comment) => void handleDeleteComment(comment)}
            onSendComment={() => void handleSendComment()}
          />
        )}
      </section>
    </div>
  );
}

function BackButton({ onBack }: Readonly<{ onBack: () => void }>) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-2 text-sm font-semibold text-black hover:underline focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
    >
      <ArrowLeft size={16} aria-hidden="true" />
      Back to Overview
    </button>
  );
}

function GuestStatusBar({
  guestInfo,
  notificationsEnabled,
  canUseNotifications,
  isSending,
  onNotificationChange,
  onSend,
}: Readonly<{
  guestInfo: ReservationWebResponse;
  notificationsEnabled: boolean;
  canUseNotifications: boolean;
  isSending: boolean;
  onNotificationChange: (checked: boolean) => void;
  onSend: () => void;
}>) {
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex gap-2" aria-label="Guest travel issue statuses">
        <IssuePill
          label={guestInfo.customFlightStatus ?? 'Flight'}
          colorCode={guestInfo.flightStatusColorCode}
          icon={<Plane size={18} aria-hidden="true" />}
        />
        <IssuePill
          label={guestInfo.customGTStatus ?? 'Ground transport'}
          colorCode={guestInfo.rentalCarStatusColorCode}
          icon={<CarFront size={18} aria-hidden="true" />}
        />
        <IssuePill
          label={guestInfo.customHotelStatus ?? 'Hotel'}
          colorCode={guestInfo.hotelStatusColorCode}
          icon={<Home size={18} aria-hidden="true" />}
        />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className={cn('inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-black', !canUseNotifications && 'cursor-not-allowed')}>
          <span className={cn('relative inline-flex h-7 w-16 shrink-0 items-center rounded-full transition-colors duration-200', notificationsEnabled ? 'bg-red-400' : 'bg-cool-gray-2', !canUseNotifications && 'opacity-50')}>
            <input
              type="checkbox"
              checked={notificationsEnabled}
              disabled={!canUseNotifications}
              onChange={(event) => onNotificationChange(event.target.checked)}
              className="sr-only"
            />
            {notificationsEnabled ? (
              <span aria-hidden="true" className="absolute left-2 text-[11px] font-bold leading-none text-white">
                ON
              </span>
            ) : (
              <span aria-hidden="true" className="absolute right-2 text-[11px] font-bold leading-none text-white">
                OFF
              </span>
            )}
            <span
              aria-hidden="true"
              className={cn(
                'absolute inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200',
                notificationsEnabled ? 'translate-x-9' : 'translate-x-1',
              )}
            />
          </span>
          <span>Notifications</span>
        </label>
        {/* Show only when eligible (isElgibleForNotification && !isNotableGuest) and not pre-confirmed */}
        {canUseNotifications && !['new', 'invited'].includes(guestInfo.status?.toLowerCase() ?? '') && (
          <button
            type="button"
            onClick={onSend}
            disabled={!notificationsEnabled || isSending}
            className="inline-flex items-center gap-2 border border-dds-green bg-dds-green px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1 disabled:cursor-not-allowed disabled:border-cool-gray-2 disabled:bg-cool-gray-2 disabled:text-cool-gray-10"
          >
            <Send size={16} aria-hidden="true" />
            {isSending ? 'Sending Notification...' : 'Send Notification'}
          </button>
        )}
      </div>
    </div>
  );
}

function InfoCard({ children, title, status, titleIcon }: Readonly<{ children: ReactNode; title: string; status?: ReactNode; titleIcon?: ReactNode }>) {
  const [expanded, setExpanded] = useState(true);
  const panelId = useId();
  return (
    <article className="h-full border border-cool-gray-2 bg-white">
      {status}
      <header className="flex items-center justify-between border-b border-cool-gray-2 bg-gray-100 px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-bold text-black">{titleIcon}{title}</h2>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => setExpanded((current) => !current)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-dds-blue hover:underline focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
        >
          {expanded ? 'Hide Details' : 'Show Details'}
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={cn('transition-transform duration-300', expanded && 'rotate-180')}
          />
        </button>
      </header>
      <div
        id={panelId}
        className="grid transition-all duration-300 ease-in-out"
        style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="p-4">{children}</div>
        </div>
      </div>
    </article>
  );
}

function InfoGrid({ items, inline }: Readonly<{ items: Array<[string, DisplayValue]>; inline?: boolean }>) {
  const visibleItems = items.filter(([, value]) => hasValue(value));
  if (visibleItems.length === 0) return <EmptyState>There is no information available</EmptyState>;

  return (
    <dl className={inline ? 'mt-4 grid gap-4 grid-cols-6' : 'mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'}>
      {visibleItems.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-xs font-semibold uppercase text-cool-gray-10">{label}</dt>
          <dd className="mt-1 break-words text-sm text-black">{displayValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function CardStatus({ label, colorCode }: Readonly<{ label: string | null | undefined; colorCode?: string | null }>) {
  const labelLower = String(label ?? '').toLowerCase();
  // 'disabled' covers both "Not Required" (XCircle) and "Successfully Booked" (CheckCircle) — use label to distinguish
  const isSuccess =
    colorCode !== 'issue' &&
    colorCode !== 'warning' &&
    (colorCode !== 'disabled' || labelLower.includes('booked') || labelLower.includes('accepted') || labelLower.includes('confirmed'));
  return (
    <div className={cn('flex items-center gap-2 border-b px-4 py-2 text-sm font-bold', issueStatusClass(colorCode))}>
      {isSuccess ? <CheckCircle size={16} aria-hidden="true" /> : <XCircle size={16} aria-hidden="true" />}
      {displayValue(label)}
    </div>
  );
}

function RegistrationCard({ guestInfo }: Readonly<{ guestInfo: ReservationWebResponse }>) {
  const accommodation = guestInfo.certainResponseObj?.registrationAccomdation;
  const stayDays =
    accommodation?.arrivalDate && accommodation?.departDate
      ? differenceInDays(parseISO(accommodation.departDate), parseISO(accommodation.arrivalDate))
      : null;
  // 'New' and 'Invited' are not yet confirmed; display as 'Pending' with neutral grey styling
  const isNew = ['new', 'invited'].includes(guestInfo.status?.toLowerCase() ?? '');
  return (
    <InfoCard title="Registration" status={<CardStatus label={isNew ? 'Pending' : guestInfo.status} colorCode={isNew ? null : guestInfo.statusColorCode} />}>
      {hasValue(guestInfo.certainResponseObj?.registrationCode) ? (
        <InfoGrid
          items={[
            ['Registration Id', guestInfo.certainResponseObj?.registrationCode],
            ['Attendee Type', guestInfo.certainResponseObj?.attendeeType],
            ['Registration Start Date', formatDate(accommodation?.arrivalDate, 'MMM d, yyyy')],
            ['Registration End Date', formatDate(accommodation?.departDate, 'MMM d, yyyy')],
            ['Registration Status', guestInfo.status],
            ['Stay Days', stayDays],
          ]}
        />
      ) : (
        <EmptyState>There is no information available</EmptyState>
      )}
    </InfoCard>
  );
}

function ReservationCard({ guestInfo, folioDataMap }: Readonly<{ guestInfo: ReservationWebResponse; folioDataMap: Map<string, GetFoliosResponse> }>) {
  const folios = guestInfo.reservationFolios ?? [];
  const reservations = guestInfo.reservationObj ?? [];
  // No hotel reservations exist yet when registration status is New or Invited
  const hasData = !['new', 'invited'].includes(guestInfo.status?.toLowerCase() ?? '') && (folios.length > 0 || reservations.length > 0);
  // Prefer backend flag; fall back to date-based computation if backend returns false
  const isBackToBack = Boolean(guestInfo.isBackToBack) || computeIsBackToBack(reservations);
  return (
    <InfoCard
      title="Reservations"
      status={<CardStatus label={getStatusLabel(guestInfo.customHotelStatus, guestInfo.hotelStatus)} colorCode={guestInfo.hotelStatusColorCode} />}
    >
      {hasData ? (
        <div className="space-y-5">
          {folios.length > 0
            ? folios.map((folio, index) => {
                const reservation = reservations[folio.index ?? index];
                const lookupKey = getFolioLookupKey(folio, reservation);
                return (
                  <FolioWithLiveData
                    key={`${folio.reservationID ?? lookupKey ?? 'reservation'}-${index}`}
                    folio={folio}
                    reservation={reservation}
                    isBackToBack={isBackToBack}
                    liveData={lookupKey ? folioDataMap.get(lookupKey) : undefined}
                  />
                );
              })
            : reservations.map((reservation, index) => (
                <FolioWithLiveData
                  key={`${reservation.reservationId ?? 'reservation'}-${index}`}
                  folio={{} as ReservationFolio}
                  reservation={reservation}
                  isBackToBack={isBackToBack}
                  liveData={folioDataMap.get(reservation.reservationId ?? '')}
                />
              ))}
        </div>
      ) : (
        <EmptyState>There is no information available</EmptyState>
      )}
    </InfoCard>
  );
}

function FolioWithLiveData({
  folio,
  reservation,
  isBackToBack,
  liveData,
}: Readonly<{
  folio: ReservationFolio;
  reservation?: HotelReservation;
  isBackToBack: boolean;
  liveData?: GetFoliosResponse | null;
}>) {
  // Prefer live value when it is a non-empty, meaningful string
  const liveStr = (live: string | null | undefined, fallback: string | null | undefined) =>
    live && hasValue(live) ? live : fallback;

  // Prefer live date when it is a valid calendar date (not DateTime.MinValue 0001-01-01)
  const liveDate = (live: string | null | undefined, fallback: string | null | undefined) => {
    if (!live) return fallback;
    const d = parseISO(live);
    return isValid(d) && d.getFullYear() > 1 ? live : fallback;
  };

  const fallbackFolio: ReservationFolio = {
    ...folio,
    hotelName: liveStr(folio.hotelName, reservation?.hotelName),
  };

  const mergedFolio: ReservationFolio = liveData
    ? {
        ...fallbackFolio,
        hotelName: liveStr(liveData.hotelName, fallbackFolio.hotelName),
        reservationStatus: liveStr(liveData.reservationStatus, fallbackFolio.reservationStatus),
        ratePlan: liveStr(liveData.ratePlan, fallbackFolio.ratePlan),
        roomType: liveStr(liveData.roomType, fallbackFolio.roomType),
        paymentType: liveStr(liveData.paymentType, fallbackFolio.paymentType),
        splRequests: liveStr(liveData.splRequests, fallbackFolio.splRequests),
        registrationUserNotes: liveStr(liveData.registrationUserNotes, fallbackFolio.registrationUserNotes),
        transactions: liveData.transactions ?? fallbackFolio.transactions,
      }
    : fallbackFolio;

  const mergedReservation: HotelReservation | undefined = liveData
    ? {
        ...reservation,
        arrival: liveDate(liveData.arrival, reservation?.arrival),
        departure: liveDate(liveData.departure, reservation?.departure),
        guestType: liveStr(liveData.guestType, reservation?.guestType),
      }
    : reservation;

  return (
    <HotelReservationSection
      folio={mergedFolio}
      reservation={mergedReservation}
      isBackToBack={isBackToBack}
    />
  );
}

function HotelReservationSection({
  folio,
  reservation,
  isBackToBack,
}: Readonly<{
  folio: ReservationFolio;
  reservation?: HotelReservation;
  isBackToBack: boolean;
}>) {
  const headingId = useId();
  const hasMeaningfulData = hasValue(folio.hotelName) || hasValue(folio.reservationStatus) || hasValue(reservation?.arrival) || hasValue(reservation?.departure);

  if (!hasMeaningfulData) {
    return <EmptyState>There is no information available</EmptyState>;
  }

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <h3 id={headingId} className="text-sm font-semibold text-black">{displayValue(folio.hotelName)}</h3>
      <InfoGrid
        items={[
          ['Hotel Name', folio.hotelName],
          ['Is Back To Back', isBackToBack ? 'Yes' : 'No'],
          ['Reservation Status', folio.reservationStatus],
          ['Rate Code', folio.ratePlan],
          ['Room Type', folio.roomType],
          ['Arrival', formatDate(reservation?.arrival)],
          ['Departure', formatDate(reservation?.departure)],
          ['Payment Method', folio.paymentType],
          ['Special Requests', folio.splRequests],
          ['Profiles Notes', folio.registrationUserNotes],
        ]}
      />
      {folio.transactions?.map((transaction, index) => (
        <InfoGrid
          key={`${transaction.folioID ?? 'folio'}-${index}`}
          items={[
            ['Folio Date', transaction.date],
            ['Folio Id', transaction.folioID],
            ['Folio', transaction.description],
            ['Folio Amount', transaction.amount],
          ]}
        />
      ))}
    </section>
  );
}

function FlightCard({ guestInfo }: Readonly<{ guestInfo: ReservationWebResponse }>) {
  const arrival = guestInfo.flightBookingObj?.arrivals?.[0];
  const departure = guestInfo.flightBookingObj?.departures?.at(-1);
  return (
    <InfoCard
      title="Flight Tickets"
      status={<CardStatus label={getStatusLabel(guestInfo.customFlightStatus, guestInfo.flightStatus)} colorCode={guestInfo.flightStatusColorCode} />}
    >
      {hasValue(guestInfo.flightBookingObj?.recordLocator) ? (
        <div className="space-y-5">
          {arrival && <FlightSection title="Arriving" segment={arrival} status={guestInfo.overallFlightStatus} />}
          {departure && <FlightSection title="Departure" segment={departure} status={guestInfo.overallFlightStatus} />}
        </div>
      ) : (
        <EmptyState>There is no information available</EmptyState>
      )}
    </InfoCard>
  );
}

function FlightSection({ title, segment, status }: Readonly<{ title: string; segment: FlightSegment; status?: string | null }>) {
  const headingId = `flight-section-${title.toLowerCase()}`;
  return (
    <section aria-labelledby={headingId}>
      <h3 id={headingId} className="text-sm font-semibold text-black">
        {title}: {displayValue(segment.departureCityCode)} - {displayValue(segment.arrivalCityCode)}
      </h3>
      <InfoGrid
        items={[
          ['Arrival', `${formatDate(segment.arrivalDate)} ${displayValue(segment.arrivalTime)}`],
          ['Departure', `${formatDate(segment.departureDate)} ${displayValue(segment.departureTime)}`],
          ['TOI', segment.travelOrderIdentifier],
          ['Status', status === 'Cancelled' ? 'Cancelled' : 'Booked'],
          ['Terminal', segment.arrivalTerminal ?? segment.departureTerminal],
        ]}
      />
    </section>
  );
}

function getGroundTransportation(guestInfo: ReservationWebResponse) {
  const directMatch =
    guestInfo.dugTbookignObj ??
    guestInfo.dugTbookingObj ??
    guestInfo.dugtBookignObj ??
    guestInfo.dugtbookignObj;
  if (directMatch) return directMatch;

  const entry = Object.entries(guestInfo).find(([key]) => {
    const normalizedKey = key.toLowerCase();
    return normalizedKey === 'dugtbookignobj' || normalizedKey === 'dugtbookingobj';
  });
  return entry?.[1] as GroundTransportationBooking | null | undefined;
}

function GroundTransportCard({ guestInfo }: Readonly<{ guestInfo: ReservationWebResponse }>) {
  const groundTransportation = getGroundTransportation(guestInfo);
  const arrival = groundTransportation?.gtArrival;
  const departure = groundTransportation?.gtDeparture;
  const hasArrivalDetails = [
    arrival?.arrivalResID,
    arrival?.arrivalPickupAndDropOff,
    arrival?.arrivalPicupAndDropOff,
    arrival?.arrivalPickupDateTime,
    arrival?.hotel,
    arrival?.arrivalVehicleType,
  ].some(hasValue);
  const hasDepartureDetails = [
    departure?.departureResID,
    departure?.departurePickupAndDropOff,
    departure?.departurePicupAndDropOff,
    departure?.departurePickupDateTime,
    departure?.hotel,
    departure?.departureVehicleType,
  ].some(hasValue);
  const hasTransportDetails = hasValue(guestInfo.certainGTSelection) || hasArrivalDetails || hasDepartureDetails;
  return (
    <InfoCard
      title="Ground Transport"
      status={<CardStatus label={getStatusLabel(guestInfo.customGTStatus, guestInfo.gtStatus)} colorCode={guestInfo.rentalCarStatusColorCode} />}
    >
      {hasTransportDetails ? (
        <div className="space-y-5">
          {hasValue(guestInfo.certainGTSelection) && (
            <div className="border border-cool-gray-2">
              <div className="flex items-center gap-2 border-b border-cool-gray-2 bg-gray-100 px-3 py-2">
                <CarFront size={16} aria-hidden="true" className="text-cool-gray-10" />
                <span className="text-sm font-medium text-cool-gray-10">Transportation Details</span>
              </div>
              <div className="p-3">
                <p className="text-sm text-black">{guestInfo.certainGTSelection}</p>
              </div>
            </div>
          )}
          {arrival && hasArrivalDetails && (
            <GroundTransportSection title="Arriving" leg={arrival} status={guestInfo.overallArrivalStatus} />
          )}
          {departure && hasDepartureDetails && (
            <GroundTransportSection title="Departure" leg={departure} status={guestInfo.overallDepartureStatus} />
          )}
        </div>
      ) : (
        <EmptyState>There is no information available</EmptyState>
      )}
    </InfoCard>
  );
}

function GroundTransportSection({
  title,
  leg,
  status,
}: Readonly<{ title: 'Arriving' | 'Departure'; leg: GroundTransportationLeg; status?: string | null }>) {
  const isArrival = title === 'Arriving';
  const headingId = `gt-section-${title.toLowerCase()}`;
  return (
    <section aria-labelledby={headingId}>
      <h3 id={headingId} className="text-sm font-semibold text-black">
        {title}: {displayValue(
          isArrival
            ? leg.arrivalPickupAndDropOff ?? leg.arrivalPicupAndDropOff
            : leg.departurePickupAndDropOff ?? leg.departurePicupAndDropOff,
        )}
      </h3>
      <InfoGrid
        items={[
          ['Status', status === 'Cancelled' ? 'Cancelled' : 'Booked'],
          ['Date & time', isArrival ? leg.arrivalPickupDateTime : leg.departurePickupDateTime],
          ['Res. No.', isArrival ? leg.arrivalResID : leg.departureResID],
          ['Drop Off', leg.hotel],
          ['Vehicle type', isArrival ? leg.arrivalVehicleType : leg.departureVehicleType],
        ]}
      />
    </section>
  );
}

function NotificationsCard({
  notifications,
  notificationCount,
  isLoading,
}: Readonly<{
  notifications: Array<{ discrepancy?: string[] | null; createdDate?: string | null }>;
  notificationCount: number;
  isLoading: boolean;
}>) {
  return (
    <InfoCard title="Notifications" titleIcon={<Mail size={16} aria-hidden="true" />}>
      {isLoading && <p className="text-sm text-cool-gray-10">Loading notifications...</p>}
      {!isLoading && notifications.length > 0 && notificationCount > 0 ? (
        <div className="space-y-4">
          {notifications.map((notification, index) => (
            <div key={`${notification.createdDate ?? 'notification'}-${index}`} className="border-b border-cool-gray-2 pb-3 last:border-b-0 last:pb-0">
              <p className="text-sm font-semibold text-black">
                {(notification.discrepancy ?? []).join(', ')} {(notification.discrepancy?.length ?? 0) > 1 ? 'Discrepancies' : 'Discrepancy'}
              </p>
              <span className="mt-2 inline-flex h-9 w-9 items-center justify-center rounded-full border border-cool-gray-2 text-cool-gray-10" aria-hidden="true">
                <CarFront size={16} />
              </span>
              <p className="mt-1 text-xs text-cool-gray-10">{formatDateTime(notification.createdDate)}</p>
            </div>
          ))}
        </div>
      ) : null}
      {!isLoading && (notifications.length === 0 || notificationCount === 0) && <EmptyState>No notifications sent</EmptyState>}
    </InfoCard>
  );
}

function CommentsCard({
  comments,
  commentText,
  currentUserEmail,
  isLoading,
  isSaving,
  onCommentTextChange,
  onDeleteComment,
  onSendComment,
}: Readonly<{
  comments: CommentResponse[];
  commentText: string;
  currentUserEmail: string;
  isLoading: boolean;
  isSaving: boolean;
  onCommentTextChange: (value: string) => void;
  onDeleteComment: (comment: CommentResponse) => void;
  onSendComment: () => void;
}>) {
  return (
    <InfoCard title="Comments" titleIcon={<MessageSquare size={16} aria-hidden="true" />}>
      <div className="max-h-[500px] space-y-4 overflow-y-auto pr-1" aria-label="Comments list">
        {isLoading && <p className="text-sm text-cool-gray-10">Loading comments...</p>}
        {!isLoading && comments.length === 0 && <EmptyState>No comments sent</EmptyState>}
        {!isLoading &&
          comments.map((comment) => (
            <div key={comment.id} className="border-b border-cool-gray-2 pb-3 last:border-b-0 last:pb-0">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-black">{displayValue(comment.fromName ?? comment.fromEmail)}</p>
                  <p className="text-xs text-cool-gray-10">{formatDateTime(comment.createdDate)}</p>
                </div>
                {SHOW_COMMENT_DELETE && comment.fromEmail?.trim().toLowerCase() === currentUserEmail.trim().toLowerCase() && (
                  <button
                    type="button"
                    onClick={() => onDeleteComment(comment)}
                    aria-label={`Delete comment from ${displayValue(comment.fromName ?? comment.fromEmail)}`}
                    className="text-sm font-semibold text-dds-red hover:underline focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
                  >
                    Delete
                  </button>
                )}
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-black">{displayValue(comment.commentText)}</p>
            </div>
          ))}
      </div>
      <div className="mt-4 space-y-3 border-t border-cool-gray-2 pt-4">
        <label htmlFor="guest-comment" className="sr-only">
          Write your comment here
        </label>
        <textarea
          id="guest-comment"
          rows={4}
          value={commentText}
          onChange={(event) => onCommentTextChange(event.target.value)}
          placeholder="Write your comment here..."
          className="w-full border border-cool-gray-2 px-3 py-2 text-sm text-black focus:border-black focus:outline-none focus:ring-0"
        />
        <button
          type="button"
          onClick={onSendComment}
          disabled={!commentText.trim() || isSaving}
          className="inline-flex w-full items-center justify-center gap-2 border border-dds-blue bg-dds-blue px-3 py-2 text-sm font-semibold text-white hover:bg-[#006895] focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1 disabled:cursor-not-allowed disabled:border-cool-gray-2 disabled:bg-cool-gray-2 disabled:text-cool-gray-10"
        >
          <MessageSquare size={16} aria-hidden="true" />
          {isSaving ? 'Send Comment...' : 'Send Comment'}
        </button>
      </div>
    </InfoCard>
  );
}

function IssuePill({
  label,
  colorCode,
  icon,
}: Readonly<{ label: string | null | undefined; colorCode?: string | null; icon: ReactNode }>) {
  return (
    <span
      className={cn('inline-flex h-9 w-9 items-center justify-center rounded-full border', issuePillClass(colorCode))}
      title={label ?? undefined}
      aria-label={String(displayValue(label))}
    >
      {icon}
    </span>
  );
}

function EmptyState({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <div className="flex items-center justify-center gap-1.5 text-sm">
      <AlertTriangle size={16} aria-hidden="true" className="shrink-0 text-dds-red" />
      <span className="text-black">{children}</span>
    </div>
  );
}